'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { Gift } from 'lucide-react';
import { fetchAPI } from '@/core/api/client';
import { cn } from '@/core/utils/cn';

interface LoyaltyEarnPreviewProps {
    /** Cart/order/item merchandise value in PLN — the amount points would be calculated from. */
    amount: number;
    /** Known once a payment method is chosen (checkout) — omit on PDP/cart where it isn't yet. */
    paymentMethod?: string;
    /** Points already being redeemed on this order, if any — redeeming disqualifies earning. */
    pointsRedeemed?: number;
    label?: string;
    className?: string;
    style?: CSSProperties;
}

/**
 * "You'll earn X points" teaser — a prospective preview, distinct from the existing
 * redeem-points widget (CartBlock/CheckoutBlock) which spends an already-earned balance.
 * Renders nothing when the loyalty program is disabled or the amount would earn 0 points.
 */
export function LoyaltyEarnPreview({ amount, paymentMethod, pointsRedeemed, label, className, style }: LoyaltyEarnPreviewProps) {
    const [points, setPoints] = useState<number | null>(null);
    const [enabled, setEnabled] = useState(false);

    useEffect(() => {
        if (!amount || amount <= 0) {
            setPoints(0);
            return;
        }
        let cancelled = false;
        const params = new URLSearchParams({ amount: String(amount) });
        if (paymentMethod) params.set('payment_method', paymentMethod);
        if (pointsRedeemed) params.set('points_redeemed', String(pointsRedeemed));
        fetchAPI(`/loyalty/earn-preview?${params.toString()}`)
            .then((data: any) => {
                if (cancelled) return;
                setPoints(data?.points ?? 0);
                setEnabled(!!data?.enabled);
            })
            .catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amount, paymentMethod, pointsRedeemed]);

    if (!enabled || !points || points <= 0) return null;

    return (
        <p
            className={cn('text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5 font-medium', className)}
            style={style}
        >
            <Gift className="h-3.5 w-3.5 shrink-0" />
            {label || 'Otrzymasz'} <strong className="font-bold">{points} pkt</strong>
        </p>
    );
}
